import React, {
    useReducer, useEffect, useRef, useState,
} from 'react';

// Components and others
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import Emojic from 'components/widgets/Emoji';
import { ReactComponent as Send } from 'assets/send.svg';
import { count } from 'sms-length';
import {
    MMS_ALLOWED_EXTENSIONS,
    MMS_FILE_SIZE_ERROR_TOOLTIP,
    MMS_MAX_ATTACHMENTS,
} from 'utils/enum/MMS';
import { isAllowedMMSFile, hasMMSFilesSizeExceeded } from 'utils/MMSUtils';

// Material UI
import {
    Tooltip, IconButton, Button, Badge, Popover,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    makeStyles,
} from '@material-ui/core';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import SentimentVerySatisfiedIcon from '@material-ui/icons/SentimentVerySatisfied';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';

export const calculateSMSInfo = (text) => {
    const messageInfo = count(text);
    const { remaining } = messageInfo;
    const numberOfSMS = messageInfo.messages;

    return { remaining, numberOfSMS };
};

export const ErrorMessageSegments = 'The SMS cannot contain more than 4 message segments';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(0, 2),
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: theme.palette.background.lavender,
    },
    inputContainer: {
        flex: 1,
        padding: theme.spacing(1, 0),
    },
    textAreaAutoSize: {
        outline: 0,
        resize: 'none',
        width: '100%',
        background: 'transparent',
        border: 'none',
    },
    iconButton: {
        padding: theme.spacing(1),
    },
    iconButtonSendMessage: {
        width: 40,
        height: 40,
        minWidth: 40,
        borderRadius: '50%',
        padding: 0,
        margin: theme.spacing(1, 0),
    },
    counterMessage: {
        fontSize: '13px',
    },
    error: {
        color: theme.palette.text.red,
        cursor: 'default',
    },
    hiddenInput: {
        display: 'none',
    },
    attachFileIcon: {
        transform: 'rotate(45deg)',
    },
    attachmentPopover: {
        padding: theme.spacing(1, 0),
        minWidth: 240,
        maxWidth: 320,
    },
    attachmentListItem: {
        paddingRight: theme.spacing(5),
    },
    addAttachmentButton: {
        margin: theme.spacing(0, 1, 0.5),
    },
}));
const ACTION_TYPE = {
    TOGGLE_EMOJI: 'toggleEmoji',
    SET_TEXT_MESSAGE: 'setTextMessage',
    SET_ATTACHMENTS: 'setAttachments',
    CLEAR_ATTACHMENTS: 'clearAttachments',
};
const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.TOGGLE_EMOJI:
        return update(state, {
            open: { $set: action.value },
        });
    case ACTION_TYPE.SET_TEXT_MESSAGE:
        return update(state, {
            text: { $set: action.value },
        });
    case ACTION_TYPE.SET_ATTACHMENTS:
        return update(state, {
            attachments: { $set: action.value },
        });
    case ACTION_TYPE.CLEAR_ATTACHMENTS:
        return update(state, {
            attachments: { $set: [] },
        });
    default:
        return state;
    }
};
const initState = {
    open: false,
    text: '',
    attachments: [],
};
const MessageAdd = ({
    onSubmit, textMessage, disabled, allowAttachments,
}) => {
    const classes = useStyles();
    const fileInputRef = useRef(null);
    const [attachmentListAnchor, setAttachmentListAnchor] = useState(null);
    const [state, dispatch] = useReducer(reducer, initState);
    const result = calculateSMSInfo(state.text);
    const attachmentCount = allowAttachments ? state.attachments.length : 0;
    const fileExceedsSize = allowAttachments && hasMMSFilesSizeExceeded(state.attachments);
    const hasContent = !StringUtils.isEmpty(state.text) || attachmentCount > 0;
    const maxAttachmentsReached = attachmentCount >= MMS_MAX_ATTACHMENTS;
    const disableSendMessage = !hasContent
        || fileExceedsSize
        || result.numberOfSMS > 4
        || disabled;

    const clearInputMessage = () => {
        dispatch({
            type: ACTION_TYPE.SET_TEXT_MESSAGE,
            value: '',
        });
        dispatch({
            type: ACTION_TYPE.CLEAR_ATTACHMENTS,
        });
        setAttachmentListAnchor(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    const sendMessage = () => {
        if (disableSendMessage) return;
        const filesToSend = allowAttachments && state.attachments.length > 0
            ? [...state.attachments]
            : [];
        onSubmit({
            text: state.text,
            files: filesToSend,
        });
        clearInputMessage();
    };
    const onChangeMessage = (value) => {
        dispatch({
            type: ACTION_TYPE.SET_TEXT_MESSAGE,
            value,
        });
    };
    const onKeyDownInputMessage = (e) => {
        // Enter sends; Shift+Enter inserts newline
        if (e.key === 'Enter' && !e.shiftKey && !disableSendMessage) {
            e.preventDefault();
            sendMessage();
        }
    };
    const toggleEmoji = () => {
        dispatch({
            type: ACTION_TYPE.TOGGLE_EMOJI,
            value: !state.open,
        });
    };
    const addEmoji = (value) => {
        dispatch({
            type: ACTION_TYPE.SET_TEXT_MESSAGE,
            value: state.text + value,
        });
    };
    const openFileDialog = () => {
        if (maxAttachmentsReached) return;
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    const closeAttachmentList = () => {
        setAttachmentListAnchor(null);
    };
    const onAttachClick = (event) => {
        if (attachmentCount > 0) {
            setAttachmentListAnchor(event.currentTarget);
            return;
        }
        openFileDialog();
    };
    const removeAttachment = (index) => {
        const updatedAttachments = state.attachments.filter((_, itemIndex) => itemIndex !== index);
        dispatch({
            type: ACTION_TYPE.SET_ATTACHMENTS,
            value: updatedAttachments,
        });
        if (updatedAttachments.length === 0) {
            closeAttachmentList();
        }
    };
    const onFileSelected = ({ target }) => {
        const selectedFiles = Array.from(target.files || []);
        // eslint-disable-next-line no-param-reassign
        target.value = '';

        if (selectedFiles.length === 0) return;

        if (maxAttachmentsReached) {
            ModalUtils.errorMessage(null, `You can attach up to ${MMS_MAX_ATTACHMENTS} files per message.`);
            return;
        }

        const remainingSlots = MMS_MAX_ATTACHMENTS - state.attachments.length;
        const validFiles = [];

        selectedFiles.forEach((file) => {
            if (validFiles.length >= remainingSlots) return;

            if (!isAllowedMMSFile(file)) {
                ModalUtils.errorMessage(null, `Only ${MMS_ALLOWED_EXTENSIONS.join(', ')} files are supported.`);
                return;
            }

            validFiles.push(file);
        });

        if (validFiles.length === 0) return;

        if (selectedFiles.length > remainingSlots) {
            ModalUtils.errorMessage(null, `You can attach up to ${MMS_MAX_ATTACHMENTS} files per message.`);
        }

        dispatch({
            type: ACTION_TYPE.SET_ATTACHMENTS,
            value: [...state.attachments, ...validFiles],
        });
    };

    useEffect(() => {
        if (!StringUtils.isEmpty(textMessage)) {
            dispatch({
                type: ACTION_TYPE.SET_TEXT_MESSAGE,
                value: textMessage,
            });
        }
    }, [textMessage]);

    const sendTooltip = fileExceedsSize
        ? MMS_FILE_SIZE_ERROR_TOOLTIP
        : 'Press enter to send a new sms';

    const acceptTypes = MMS_ALLOWED_EXTENSIONS.join(',');

    const attachButton = (
        <Badge
            badgeContent={attachmentCount}
            color="primary"
            invisible={attachmentCount === 0}
        >
            <IconButton
                onClick={onAttachClick}
                className={classes.iconButton}
            >
                <AttachFileIcon className={classes.attachFileIcon} />
            </IconButton>
        </Badge>
    );

    return (
        <div
            className={classes.root}
        >
            <div className={classes.inputContainer}>
                {state.open && (
                    <Emojic
                        onChange={addEmoji}
                        onClose={toggleEmoji}
                    />
                )}
                <TextareaAutosize
                    rows={1}
                    rowsMax={6}
                    maxLength={550}
                    value={state.text}
                    placeholder="Enter your message"
                    className={classes.textAreaAutoSize}
                    onKeyDown={onKeyDownInputMessage}
                    onChange={(e) => onChangeMessage(e.target.value)}
                />
            </div>
            <Tooltip title={result.numberOfSMS > 4 ? ErrorMessageSegments : ''}>
                <div className={clsx(classes.counterMessage, { [classes.error]: result.numberOfSMS > 4 })}>{`${result.remaining} / ${result.numberOfSMS}`}</div>
            </Tooltip>
            <Tooltip title="Emoji">
                <IconButton
                    onClick={toggleEmoji}
                    className={classes.iconButton}
                >
                    <SentimentVerySatisfiedIcon />
                </IconButton>
            </Tooltip>
            {allowAttachments && (
                <>
                    {attachmentCount > 0 ? attachButton : (
                        <Tooltip title="Attach file">
                            {attachButton}
                        </Tooltip>
                    )}
                    <Popover
                        open={Boolean(attachmentListAnchor)}
                        anchorEl={attachmentListAnchor}
                        onClose={closeAttachmentList}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                    >
                        <div className={classes.attachmentPopover}>
                            <List dense disablePadding>
                                {state.attachments.map((file, index) => (
                                    <ListItem key={`${file.name}-${index}`} className={classes.attachmentListItem}>
                                        <ListItemText
                                            primary={file.name}
                                            primaryTypographyProps={{ noWrap: true }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={() => removeAttachment(index)}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                            {!maxAttachmentsReached && (
                                <Button
                                    size="small"
                                    color="primary"
                                    className={classes.addAttachmentButton}
                                    onClick={openFileDialog}
                                >
                                    Add file
                                </Button>
                            )}
                        </div>
                    </Popover>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={acceptTypes}
                        className={classes.hiddenInput}
                        onChange={onFileSelected}
                    />
                </>
            )}
            <Tooltip title={sendTooltip}>
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={disableSendMessage}
                        className={classes.iconButtonSendMessage}
                        onClick={sendMessage}
                    >
                        <Send />
                    </Button>
                </span>
            </Tooltip>
        </div>
    );
};

MessageAdd.propTypes = {
    textMessage: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    allowAttachments: PropTypes.bool,
};

MessageAdd.defaultProps = {
    textMessage: null,
    disabled: false,
    allowAttachments: false,
};

export default MessageAdd;
